/**
 * Debug Dummy MIDI — computer-keyboard → MIDI mapping.
 *
 * Gated behind FeatureFlag.DEBUG_DUMMY_MIDI. Lets a developer play notes with a
 * regular computer keyboard when no physical MIDI device is available.
 *
 * Layout (mirrors a piano keyboard, GarageBand-style, anchored so A→E3):
 *
 *   White keys — QWERTY *home row*, left→right, ascending naturals from the
 *   anchor:
 *     A  S  D  F  G  H  J  K  L  ;
 *
 *   Black keys — the *row above* (QWERTY row), each sitting in the gap between
 *   the two home-row white keys it physically straddles. Only the gaps that
 *   actually contain a black key are mapped, so — exactly like a real keyboard —
 *   some top-row keys are intentionally left unmapped (the E→F and B→C gaps).
 *
 * Transpose is *diatonic*, not chromatic: it slides the whole window by whole
 * white-key steps and regenerates the black-key gaps at the new position. This
 * keeps the invariant that the home row is ALWAYS white keys and the top row is
 * ALWAYS the black keys in between — a plain semitone shift would land home-row
 * keys on black keys, which a fixed physical keyboard can't represent.
 *
 * Keys are identified by KeyboardEvent.code (physical position) so the mapping
 * is independent of the user's keyboard layout (QWERTY/AZERTY/etc.).
 */
import { getMidiNumberFromNote } from 'Phaser/Util/OSMDHelper';

/** Fixed velocity for injected note-on events (the feature carries no real velocity). */
export const DEBUG_MIDI_VELOCITY = 100;

/** The note the 'A' key maps to at transpose 0. Must be a white (natural) key. */
export const DEBUG_MIDI_ANCHOR_NOTE = 'E3';

/**
 * Home-row physical keys, left→right — one white key each.
 */
export const DEBUG_MIDI_HOME_ROW_CODES = [
  'KeyA',
  'KeyS',
  'KeyD',
  'KeyF',
  'KeyG',
  'KeyH',
  'KeyJ',
  'KeyK',
  'KeyL',
  'Semicolon',
] as const;

/**
 * Top-row physical keys, left→right. Entry i sits in the gap between home-row
 * white keys i and i+1 (W between A&S, E between S&D, …, P between L&;), so it
 * only produces a note when that gap actually contains a black key.
 */
const DEBUG_MIDI_TOP_ROW_CODES = [
  'KeyW',
  'KeyE',
  'KeyR',
  'KeyT',
  'KeyY',
  'KeyU',
  'KeyI',
  'KeyO',
  'KeyP',
] as const;

const BASE_ANCHOR_MIDI = getMidiNumberFromNote(DEBUG_MIDI_ANCHOR_NOTE);

/** Pitch classes of the white (natural) keys: C D E F G A B. */
const WHITE_PITCH_CLASSES = new Set([0, 2, 4, 5, 7, 9, 11]);

const isWhiteKey = (midi: number): boolean => WHITE_PITCH_CLASSES.has(((midi % 12) + 12) % 12);

/** Next white key strictly in the given direction (+1 up, -1 down). */
const stepToWhiteKey = (midi: number, direction: 1 | -1): number => {
  let next = midi + direction;
  while (!isWhiteKey(next)) next += direction;
  return next;
};

/** Move `steps` whole white keys from `midi` (positive = up, negative = down). */
const shiftWhiteKeys = (midi: number, steps: number): number => {
  const direction: 1 | -1 = steps >= 0 ? 1 : -1;
  let result = midi;
  for (let i = 0; i < Math.abs(steps); i++) {
    result = stepToWhiteKey(result, direction);
  }
  return result;
};

const inMidiRange = (midi: number): boolean => midi >= 0 && midi <= 127;

/**
 * Build the KeyboardEvent.code → MIDI-number map for the given diatonic
 * transpose (in white-key steps). Notes that would fall outside the valid MIDI
 * range (0–127) are omitted.
 */
export function buildDebugMidiKeyboardMap(transposeWhiteSteps = 0): Record<string, number> {
  const anchor = shiftWhiteKeys(BASE_ANCHOR_MIDI, transposeWhiteSteps);

  // Home row: successive white keys from the anchor.
  const whiteKeys: number[] = [];
  let current = anchor;
  for (let i = 0; i < DEBUG_MIDI_HOME_ROW_CODES.length; i++) {
    whiteKeys.push(current);
    current = stepToWhiteKey(current, 1);
  }

  const map: Record<string, number> = {};
  DEBUG_MIDI_HOME_ROW_CODES.forEach((code, i) => {
    if (inMidiRange(whiteKeys[i])) map[code] = whiteKeys[i];
  });

  // Top row: the black key in each gap that spans a whole tone (two white keys a
  // whole step apart). Semitone gaps (E–F, B–C) have no black key → unmapped.
  DEBUG_MIDI_TOP_ROW_CODES.forEach((code, i) => {
    const low = whiteKeys[i];
    const high = whiteKeys[i + 1];
    if (high - low === 2) {
      const blackKey = low + 1;
      if (inMidiRange(blackKey)) map[code] = blackKey;
    }
  });

  return map;
}
